/**
 * InviteDialog - Dialog for inviting users/groups to a message thread
 **/

import React, { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Tooltip } from 'primereact/tooltip';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { useInviteUsers } from '../../hooks/useMessageMutations';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';
import { getClientUserList } from '../../services/messageService';

// ── Helpers ───────────────────────────────────────────────────────────────
function computeUsersNotInCase(selectedUsers, caseLinkedUsers) {
  const individuals = selectedUsers.filter((u) => {
    if (u?.res_type === 'user-group') return false; // handled separately
    const linked = caseLinkedUsers?.find((l) => {
      if (u?.client_cognito_username) return u.client_cognito_username === l?.client_cognito_username;
      return u?.contact_id && u.contact_id === l?.contact_id;
    });
    return !linked; // not in case → conflict
  });

  const groups = selectedUsers.filter((u) => u?.res_type === 'user-group');

  return [...individuals, ...groups];
}

// ── Component ─────────────────────────────────────────────────────────────

function InviteDialog({ messageDetails, caseUserClients, showInviteDialog, setShowInviteDialog }) {
  const { addToast } = useToast();
  const inviteMutation = useInviteUsers();
  const userContext = useUserDetailsContext();
  const currentUserId = userContext?.firmDetails?.current_user;

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [userError, setUserError] = useState('');

  const [showConflict, setShowConflict] = useState(false);
  const [usersNotInCase, setUsersNotInCase] = useState([]);

  // ── Fetch all users when dialog opens ────────────────────────────────────
  useEffect(() => {
    if (!showInviteDialog) return;
    getClientUserList({
      is_include_groups: true,
      active_groups: true,
      excludeFromLoading: true,
    }).then((res) => {
      setAllUsers(res?.data || []);
    });
  }, [showInviteDialog]);

  // ── Search ────────────────────────────────────────────────────────────────
  const searchUsers = useCallback(
    (event) => {
      const query = (event?.query || '').toLowerCase().trim();
      const existingIds = messageDetails?.accessible_list || [];

      const available = (allUsers || []).reduce((acc, user) => {
        const userId = user?.client_cognito_username || user?.contact_id || user?.user_group_id;
        if (existingIds.includes(userId)) return acc;
        if (selectedUsers.some((s) => (s?.client_cognito_username || s?.contact_id || s?.user_group_id) === userId)) return acc;

        const displayName = generateFullName(user) || '';
        if (query && !displayName.toLowerCase().includes(query)) return acc;

        acc.push({
          ...user,
          recipient_name: displayName,
          recipient_id: user?.client_cognito_username || user?.contact_id,
        });
        return acc;
      }, []);

      setFilteredList(available);
    },
    [allUsers, messageDetails, selectedUsers]
  );

  // ── Per-item validation on select ─────────────────────────────────────────
  const onSelectUser = useCallback(
    (e) => {
      const item = e.value;

      if (item?.res_type === 'user-group') {
        const alreadyInThread = (messageDetails?.groups || []).some((g) => g?.group_id === item?.user_group_id || g?.sk === item?.sk);
        if (alreadyInThread) {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'This group is already in the conversation');
          setSelectedUsers((prev) => prev.filter((u) => u?.user_group_id !== item?.user_group_id));
          return;
        }
      } else {
        // Prevent duplicate (double-guard beyond search filter)
        const existingIds = [...(messageDetails?.firm_users || []), ...(messageDetails?.contacts || [])].map(
          (u) => u?.firm_user_id || u?.contact_id
        );
        const userId = item?.firm_user_id || item?.contact_id;
        if (userId && existingIds.includes(userId)) {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'This user is already part of the conversation');
          setSelectedUsers((prev) => prev.filter((u) => (u?.firm_user_id || u?.contact_id) !== userId));
          return;
        }
      }
    },
    [messageDetails, currentUserId]
  );

  // ── Submit: run conflict check first ─────────────────────────────────────
  const handleInviteClick = useCallback(() => {
    if (selectedUsers.length === 0) {
      setUserError('Please select at least one user or group');
      return;
    }

    const conflicts = computeUsersNotInCase(selectedUsers, caseUserClients);

    if (conflicts.length > 0) {
      setUsersNotInCase(conflicts);
      setShowConflict(true);
      return;
    }

    doInvite();
  }, [selectedUsers, caseUserClients]);

  // ── The actual API call ───────────────────────────────────────────────────
  const doInvite = useCallback(() => {
    const users = [];
    const groups = [];

    selectedUsers.forEach((item) => {
      if (item?.res_type === 'user-group') {
        groups.push({ group_id: item.user_group_id, group_name: item.group_name, sk: item.sk });
      } else {
        users.push(item);
      }
    });

    inviteMutation.mutate(
      {
        message_thread_id: messageDetails?.message_thread_id,
        sk: `message#${messageDetails?.message_thread_id}`,
        isLead: messageDetails?.is_lead,
        data: { invited_user: users, invited_group: groups },
      },
      {
        onSuccess: (response) => {
          addToast(
            false,
            toastConstant.toasterType.SUCCESS,
            toastConstant.api.SUCCESS,
            response?.data?.body || 'Users invited successfully'
          );
          handleHide();
        },
        onError: (err) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.response?.data || 'Failed to invite users');
          setShowConflict(false);
        },
      }
    );
  }, [inviteMutation, messageDetails, selectedUsers]);

  // ── Close / reset ─────────────────────────────────────────────────────────
  const handleHide = useCallback(() => {
    setShowInviteDialog(false);
    setAllUsers([]);
    setSelectedUsers([]);
    setFilteredList([]);
    setUserError('');
    setShowConflict(false);
    setUsersNotInCase([]);
  }, [setShowInviteDialog]);

  // ── Route helpers for conflict user links ──────────────────────────────────
  const getUserRoute = (user) => {
    if (user?.res_type === 'user-group') return '/settings/groups';
    if (user?.access_level?.toLowerCase() === 'client') return user?.contact_id ? `/contacts/${user.contact_id}` : '/contacts';
    return user?.firm_user_id ? `/settings/team-management/${user.firm_user_id}` : '/settings/team-management';
  };

  // ── Templates ─────────────────────────────────────────────────────────────
  const itemTemplate = (item) => (
    <div className="d-flex align-items-center">
      {item?.res_type === 'user-group' ? (
        <span>
          <i className="pi pi-users me-2" />
          {item.group_name}
        </span>
      ) : (
        <span>
          {item?.firm_user_id && <i className="fas fa-user-tie me-2" />}
          {generateFullName(item)}
          {item?.access_level ? ` (${item.access_level})` : ''}
        </span>
      )}
    </div>
  );

  const selectedItemTemplate = (item) => <span>{item?.group_name || generateFullName(item) || 'Unknown'}</span>;

  // ── Footer: changes per step ───────────────────────────────────────────────
  const footer = showConflict ? (
    <div className="d-flex justify-content-end gap-2">
      <Button label="Cancel" className="p-button-secondary outline" onClick={() => setShowConflict(false)} />
      <Button
        label="Yes, Invite Users"
        icon="pi pi-check"
        className="p-button-secondary"
        onClick={doInvite}
        disabled={inviteMutation.isPending}
        loading={inviteMutation.isPending}
      />
    </div>
  ) : (
    <div className="d-flex justify-content-end gap-2">
      <Button label="Cancel" className="p-button-secondary outline" onClick={handleHide} />
      <Button
        label="Invite Users"
        icon="pi pi-check"
        className="p-button-secondary"
        onClick={handleInviteClick}
        disabled={inviteMutation.isPending}
        loading={inviteMutation.isPending}
      />
    </div>
  );

  return (
    <Dialog
      header={showConflict ? 'User Conflict' : 'Invite Users'}
      visible={showInviteDialog}
      onHide={handleHide}
      style={{ width: '40vw', minWidth: '360px' }}
      className="common-popup-style"
      footer={footer}
      modal
    >
      {showConflict ? (
        /* ── Step 2: Conflict confirmation ──────────────────────────────── */
        <>
          <p className="mb-2">
            The following users are not linked to the case:{' '}
            <Link
              to={`/${messageDetails?.is_lead ? 'leads' : 'cases'}/${messageDetails?.case?.case_id || ''}`}
              className="fw-bold text-primary-dark text-decoration-none case-details-link"
            >
              {messageDetails?.case?.case_name || 'N/A'}
            </Link>
          </p>
          <ul className="mb-3">
            {usersNotInCase.map((user, i) => (
              <li key={i}>
                <Link
                  to={getUserRoute(user)}
                  className="text-primary-dark text-decoration-none case-details-link redirection-link-no-text-transform"
                >
                  {user?.fullName || generateFullName(user) || user?.group_name || 'Unknown'}(
                  {user?.access_level || (user?.res_type === 'user-group' ? 'Group' : 'Client')})
                </Link>
              </li>
            ))}
          </ul>
          <p className="mb-0">Do you still want to invite the selected users to this message?</p>
        </>
      ) : (
        /* ── Step 1: User search ─────────────────────────────────────────── */
        <>
          <p className="mb-2">Type the name of users or groups to invite to this conversation</p>
          <AutoComplete
            name="user"
            id="user-search"
            placeholder="Search User"
            value={selectedUsers}
            suggestions={filteredList}
            completeMethod={searchUsers}
            field="fullName"
            className="w-100 invite-user mb-1 input-shadow"
            multiple
            onSelect={onSelectUser}
            onChange={(e) => {
              setSelectedUsers(e.value);
              setUserError('');
            }}
            itemTemplate={itemTemplate}
            selectedItemTemplate={selectedItemTemplate}
            aria-label="Search for users or groups to invite"
          />
          {userError && <span className="text-danger d-block mt-1 F-size13">{userError}</span>}
          <div className="d-flex mt-2 flex-wrap">
            {selectedUsers?.length > 0
              ? selectedUsers?.map((item) => {
                  const chipId = item?.recipient_id || item?.user_group_id;
                  const tooltipTarget = `invite-chip-${chipId}`;
                  return (
                    <React.Fragment key={chipId}>
                      <Tooltip target={`.${tooltipTarget}`} content={item?.recipient_name} showDelay={200} />
                      <Chip
                        label={item?.recipient_name}
                        className={`custom-chip tag p-mb-2 me-2 my-1 ${tooltipTarget}`}
                        style={{ maxWidth: '150px' }}
                        removable
                        onRemove={() => console.log(item)}
                        template={
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              maxWidth: '120px',
                            }}
                          >
                            {item?.recipient_name}
                          </span>
                        }
                      />
                    </React.Fragment>
                  );
                })
              : null}
          </div>
        </>
      )}
    </Dialog>
  );
}

InviteDialog.propTypes = {
  messageDetails: PropTypes.shape({
    message_thread_id: PropTypes.string,
    is_lead: PropTypes.bool,
    pk: PropTypes.string,
    accessible_list: PropTypes.arrayOf(PropTypes.string),
    groups: PropTypes.arrayOf(PropTypes.shape({ group_id: PropTypes.string, sk: PropTypes.string })),
    case: PropTypes.shape({ case_id: PropTypes.string, case_name: PropTypes.string }),
  }),
  caseUserClients: PropTypes.arrayOf(PropTypes.object),
  showInviteDialog: PropTypes.bool.isRequired,
  setShowInviteDialog: PropTypes.func.isRequired,
};

InviteDialog.defaultProps = {
  messageDetails: null,
  caseUserClients: [],
};

export default memo(InviteDialog);
